/**
 * FormacionViewer — Visualización de solo lectura.
 * El color de cada celda/chip indica el estado de asistencia:
 *   confirmado           → verde
 *   no_asiste            → rojo
 *   cubierto_refuerzo    → naranja (baja cubierta por un refuerzo)
 *   pendiente            → gris azulado
 */
import React, { useMemo } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { Box,
  Flex,
  Grid,
  Icon,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import { FaCheck, FaTimes, FaQuestion, FaExchangeAlt } from 'react-icons/fa';
import {
  GiClarinet, GiFlute, GiSaxophone, GiTrombone, GiTrumpet, GiTuba,
  GiViolin, GiGuitar, GiFrenchHorn, GiAccordion, GiPianoKeys,
  GiDrum, GiMusicalNotes,
} from 'react-icons/gi';
import { IconoBomboMarcha } from './icons/IconoBomboMarcha';
import { IconoPlatillos } from './icons/IconoPlatillos';
import { primeraFamiliaMusico } from '../utils/musicoInstrumentos';

// ── Paleta de asistencia ─────────────────────────────────────────────────────
const ASIST = {
  confirmado: {
    bg:     '#F0FFF4',
    border: '#38A169',
    text:   '#22543D',
    icon:   FaCheck,
    iconBg: '#38A169',
    dark: { bg: '#1A3D2B', border: '#48BB78', text: '#9AE6B4' },
  },
  no_asiste: {
    bg:     '#FFF5F5',
    border: '#E53E3E',
    text:   '#742A2A',
    icon:   FaTimes,
    iconBg: '#E53E3E',
    dark: { bg: '#3D1414', border: '#FC8181', text: '#FEB2B2' },
  },
  cubierto_refuerzo: {
    bg:     '#FFFAF0',
    border: '#DD6B20',
    text:   '#7B341E',
    icon:   FaExchangeAlt,
    iconBg: '#DD6B20',
    dark: { bg: '#3D2010', border: '#F6AD55', text: '#FBD38D' },
  },
  pendiente: {
    bg:     '#EDF2F7',
    border: '#A0AEC0',
    text:   '#2D3748',
    icon:   FaQuestion,
    iconBg: '#A0AEC0',
    dark: { bg: '#1A202C', border: '#718096', text: '#CBD5E0' },
  },
};

const EMPTY_CELL = {
  bg: '#F7FAFC', border: '#E2E8F0', text: '#A0AEC0',
  dark: { bg: '#171923', border: '#2D3748', text: '#4A5568' },
};

// ── Iconos de instrumento ────────────────────────────────────────────────────
const INSTRUMENTO_ICON_MAP = {
  'trompeta': GiTrumpet, 'corneta': GiTrumpet, 'cornetin': GiTrumpet, 'fliscorno': GiTrumpet, 'bugle': GiTrumpet,
  'trombon': GiTrombone, 'trombon tenor': GiTrombone, 'trombon bajo': GiTrombone,
  'trompa': GiFrenchHorn, 'corno': GiFrenchHorn, 'corno frances': GiFrenchHorn,
  'tuba': GiTuba, 'sousafon': GiTuba, 'helicon': GiTuba, 'bombardino': GiTuba, 'euphonium': GiTuba,
  'saxofon': GiSaxophone, 'saxofon soprano': GiSaxophone, 'saxofon alto': GiSaxophone,
  'saxofon tenor': GiSaxophone, 'saxofon baritono': GiSaxophone,
  'clarinete': GiClarinet,
  'flauta': GiFlute, 'flautin': GiFlute, 'piccolo': GiFlute,
  'bombo': IconoBomboMarcha, 'bombo con platos': IconoBomboMarcha, 'bombo y platos': IconoBomboMarcha,
  'caja': GiDrum, 'redoblante': GiDrum, 'tambor': GiDrum, 'tambo': GiDrum,
  'platos': IconoPlatillos, 'platillos': IconoPlatillos,
  'violin': GiViolin, 'viola': GiViolin, 'violonchelo': GiViolin,
  'guitarra': GiGuitar,
  'piano': GiPianoKeys, 'teclado': GiPianoKeys,
  'acordeon': GiAccordion,
};

function normNombre(n) {
  return (n || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim();
}

function resolveMusicoIa(musico, pos) {
  const rows = musico?.instrumentosAsignados;
  if (!rows?.length) return null;
  if (pos?.instrumentoId || pos?.instrumentoPersonalizadoId) {
    return (
      rows.find(
        (x) =>
          x.instrumento?.id === pos.instrumentoId ||
          x.instrumentoPersonalizado?.id === pos.instrumentoPersonalizadoId,
      ) || rows[0]
    );
  }
  return rows[0];
}

function getInstrIcon(nombre) {
  if (nombre) {
    const key = normNombre(nombre);
    if (INSTRUMENTO_ICON_MAP[key]) return INSTRUMENTO_ICON_MAP[key];
    for (const [k, ico] of Object.entries(INSTRUMENTO_ICON_MAP)) {
      if (key.startsWith(k)) return ico;
    }
  }
  return GiMusicalNotes;
}

// ── Celda de músico con asistencia ──────────────────────────────────────────
function CeldaMusico({ musico, asistencia, refuerzo, pos }) {
  const isDark = useColorModeValue(false, true);

  const estaCubierto = asistencia === 'no_asiste' && !!refuerzo;
  const pal = estaCubierto ? ASIST.cubierto_refuerzo : (ASIST[asistencia] || ASIST.pendiente);
  const bg     = isDark ? pal.dark.bg     : pal.bg;
  const border = isDark ? pal.dark.border  : pal.border;
  const text   = isDark ? pal.dark.text    : pal.text;
  const AsistIcon = pal.icon;

  const ia = resolveMusicoIa(musico, pos);
  const iaName = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || null;
  const iaVoz = ia?.vozPartituraPersonalizada?.nombre || null;
  const InstrIco = getInstrIcon(iaName);
  const apellidoCorto = musico.apellidos
    ? ` ${musico.apellidos.trim().split(/\s+/)[0][0]}.`
    : '';
  const refNombre = refuerzo
    ? [refuerzo.contactoAgenda?.nombre || refuerzo.nombre, refuerzo.contactoAgenda?.apellidos].filter(Boolean).join(' ')
    : null;

  return (
    <Tooltip
      disabled={!estaCubierto}
      content={`Cubierto por ${refNombre}`}
      showArrow
      positioning={{
        placement: "top"
      }}
    >
      <VStack
        gap={1}
        align="center"
        bg={bg}
        border="2.5px solid"
        borderColor={border}
        rounded="xl"
        p={2}
        h="full"
        minH="96px"
        justify="center"
        position="relative"
        shadow="sm"
      >
        {/* Icono de asistencia — grande y prominente arriba */}
        <Box
          bg={pal.iconBg}
          rounded="full"
          w="28px"
          h="28px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          shadow="md"
          flexShrink={0}
        >
          <Icon color="white" boxSize="13px" asChild><AsistIcon /></Icon>
        </Box>

        {/* Icono de instrumento — pequeño, informativo */}
        <Icon boxSize="18px" color={border} opacity={0.7} asChild><InstrIco /></Icon>

        {/* Nombre del músico original (tachado si está cubierto) */}
        <Text
          fontSize="2xs"
          fontWeight="800"
          textAlign="center"
          lineClamp={2}
          color={text}
          lineHeight="1.2"
          px={0.5}
          textDecoration={estaCubierto ? 'line-through' : 'none'}
          opacity={estaCubierto ? 0.65 : 1}
        >
          {musico.nombre}{apellidoCorto}
        </Text>

        {/* Nombre del refuerzo sustituto */}
        {estaCubierto && refNombre && (
          <Text fontSize="2xs" fontWeight="800" color={border} textAlign="center" lineClamp={2} lineHeight="1.2">
            ↳ {refNombre}
          </Text>
        )}

        {/* Instrumento */}
        {iaName && !estaCubierto && (
          <Text fontSize="2xs" color={border} textAlign="center" lineClamp={1} opacity={0.85}>
            {iaName}
          </Text>
        )}
        {iaVoz && !estaCubierto && (
          <Text fontSize="2xs" color={border} textAlign="center" lineClamp={1} opacity={0.75} fontStyle="italic">
            {iaVoz}
          </Text>
        )}
      </VStack>
    </Tooltip>
  );
}

// ── Celda vacía con posición de instrumento definida ────────────────────────
function CeldaVacia({ instName, isDark }) {
  const pal = EMPTY_CELL;
  const bg     = isDark ? pal.dark.bg     : pal.bg;
  const border = isDark ? pal.dark.border  : pal.border;
  const text   = isDark ? pal.dark.text    : pal.text;
  const InstrIco = getInstrIcon(instName);

  return (
    <VStack
      gap={1}
      align="center"
      bg={bg}
      border="2px dashed"
      borderColor={border}
      rounded="xl"
      p={2}
      h="full"
      minH="96px"
      justify="center"
      opacity={0.65}
    >
      <Icon boxSize="20px" color={text} asChild><InstrIco /></Icon>
      {instName && (
        <Text fontSize="2xs" color={text} textAlign="center" lineClamp={2}>{instName}</Text>
      )}
    </VStack>
  );
}

// ── Vista GRID ───────────────────────────────────────────────────────────────
function GridReadOnly({ plantilla, posiciones, refuerzoMap }) {
  const isDark = useColorModeValue(false, true);
  const emptyCellBg = isDark ? EMPTY_CELL.dark.bg : EMPTY_CELL.bg;
  const emptyCellBorder = isDark ? EMPTY_CELL.dark.border : EMPTY_CELL.border;

  const { filas, columnas, tieneDirector, tieneFilaExtra,
          filaExtraCeldas = 6, filaExtraPos = 'before', filaExtraEtiqueta } = plantilla;

  const posMap = useMemo(() => {
    const m = {};
    for (const p of posiciones) m[`${p.fila}-${p.columna}`] = p;
    return m;
  }, [posiciones]);

  const renderCell = (fila, col, key) => {
    const pos = posMap[`${fila}-${col}`];
    const musico = pos?.musico;
    const asistencia = pos?.asistencia;
    const instName = pos?.instrumento?.nombre || pos?.instrumentoPersonalizado?.nombre;
    const refuerzo = pos?.id ? refuerzoMap?.[pos.id] : null;

    return (
      <Box key={key} h="full">
        {musico ? (
          <CeldaMusico musico={musico} asistencia={asistencia} refuerzo={refuerzo} pos={pos} />
        ) : instName ? (
          <CeldaVacia instName={instName} isDark={isDark} />
        ) : (
          <Box
            bg={emptyCellBg}
            border="2px dashed"
            borderColor={emptyCellBorder}
            rounded="xl"
            minH="96px"
            h="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            opacity={0.3}
          >
            <Text fontSize="xs" color={isDark ? 'gray.600' : 'gray.300'}>·</Text>
          </Box>
        )}
      </Box>
    );
  };

  const renderFilaExtra = () => (
    <Flex gap={2} justify="center" mb={2} wrap="wrap" align="center">
      <Text fontSize="xs" color="gray.500" fontStyle="italic" flexShrink={0}>
        {filaExtraEtiqueta || 'Fila extra'}
      </Text>
      <Grid templateColumns={`repeat(${filaExtraCeldas}, minmax(80px, 1fr))`} gap={2}>
        {Array.from({ length: filaExtraCeldas }, (_, c) => renderCell(-2, c, `extra-${c}`))}
      </Grid>
    </Flex>
  );

  const renderDirector = () => (
    <Flex justify="center" mb={2}>
      <Box w="120px">
        {renderCell(-1, 0, 'director')}
      </Box>
    </Flex>
  );

  return (
    <VStack gap={2} align="stretch">
      {tieneFilaExtra && filaExtraPos === 'before' && renderFilaExtra()}
      {tieneDirector && renderDirector()}
      <Grid templateColumns={`repeat(${columnas}, 1fr)`} gap={2}>
        {Array.from({ length: filas }, (_, r) =>
          Array.from({ length: columnas }, (_, c) => renderCell(r, c, `${r}-${c}`))
        )}
      </Grid>
      {tieneFilaExtra && filaExtraPos === 'after' && renderFilaExtra()}
    </VStack>
  );
}

// ── Guías del lienzo libre (mismas que FormacionEditor) ──────────────────────
function CanvasGuiaTeatro() {
  return (
    <Box position="absolute" inset={0} w="100%" h="100%" opacity={0.13} pointerEvents="none" color="purple.400" asChild><svg>
        {[85, 70, 55, 40].map(r => (
          <ellipse key={r} cx="50%" cy="95%" rx={`${r}%`} ry={`${r * 0.55}%`}
            fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 4" />
        ))}
        <line x1="5%" y1="90%" x2="95%" y2="90%" stroke="currentColor" strokeWidth="2" />
      </svg></Box>
  );
}

function CanvasGuiaCalle() {
  return (
    <Box position="absolute" inset={0} w="100%" h="100%" pointerEvents="none" color="purple.400" asChild><svg>
        <rect x="2%" y="0" width="12%" height="100%" fill="currentColor" opacity={0.06} rx="4" />
        <line x1="14%" y1="0" x2="14%" y2="100%" stroke="currentColor" strokeWidth="2" opacity={0.18} strokeDasharray="8 5" />
        <rect x="86%" y="0" width="12%" height="100%" fill="currentColor" opacity={0.06} rx="4" />
        <line x1="86%" y1="0" x2="86%" y2="100%" stroke="currentColor" strokeWidth="2" opacity={0.18} strokeDasharray="8 5" />
        <line x1="50%" y1="5%" x2="50%" y2="95%" stroke="currentColor" strokeWidth="1" opacity={0.1} strokeDasharray="12 8" />
        <g opacity={0.22}>
          <line x1="50%" y1="85%" x2="50%" y2="12%" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
          <polyline points="44%,22% 50%,10% 56%,22%" fill="none" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        </g>
      </svg></Box>
  );
}

// ── Chip de músico para modo LIBRE ───────────────────────────────────────────
function MusicoChipLibre({ musico, asistencia, refuerzo, pos }) {
  const isDark = useColorModeValue(false, true);
  const estaCubierto = asistencia === 'no_asiste' && !!refuerzo;
  const pal = estaCubierto ? ASIST.cubierto_refuerzo : (ASIST[asistencia] || ASIST.pendiente);
  const bg     = isDark ? pal.dark.bg     : pal.bg;
  const border = isDark ? pal.dark.border  : pal.border;
  const text   = isDark ? pal.dark.text    : pal.text;
  const AsistIcon = pal.icon;

  const ia = resolveMusicoIa(musico, pos);
  const iaName = ia?.instrumento?.nombre || ia?.instrumentoPersonalizado?.nombre || null;
  const iaVoz = ia?.vozPartituraPersonalizada?.nombre || null;
  const InstrIco = getInstrIcon(iaName);
  const apellidoCorto = musico.apellidos
    ? ` ${musico.apellidos.trim().split(/\s+/)[0][0]}.`
    : '';
  const refNombre = refuerzo
    ? [refuerzo.contactoAgenda?.nombre || refuerzo.nombre, refuerzo.contactoAgenda?.apellidos].filter(Boolean).join(' ')
    : null;

  return (
    <Tooltip
      disabled={!estaCubierto}
      content={`Cubierto por ${refNombre}`}
      showArrow
      positioning={{
        placement: "top"
      }}
    >
      <Box
        bg={bg}
        border="2.5px solid"
        borderColor={border}
        rounded="xl"
        px={2}
        py={1.5}
        minW="80px"
        maxW="110px"
        shadow="md"
        position="relative"
      >
        {/* Icono asistencia grande arriba-derecha */}
        <Box
          position="absolute"
          top="-10px"
          right="-10px"
          bg={pal.iconBg}
          rounded="full"
          w="24px"
          h="24px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          shadow="md"
          zIndex={2}
        >
          <Icon color="white" boxSize="11px" asChild><AsistIcon /></Icon>
        </Box>

        <VStack gap={0} align="center">
          <Icon boxSize="18px" color={border} opacity={0.8} asChild><InstrIco /></Icon>
          <Text
            fontSize="2xs"
            fontWeight="700"
            textAlign="center"
            lineClamp={1}
            color={text}
            textDecoration={estaCubierto ? 'line-through' : 'none'}
            opacity={estaCubierto ? 0.65 : 1}
          >
            {musico.nombre}{apellidoCorto}
          </Text>
          {estaCubierto && refNombre && (
            <Text fontSize="2xs" fontWeight="800" color={border} textAlign="center" lineClamp={2} lineHeight="1.2">
              ↳ {refNombre}
            </Text>
          )}
          {iaName && !estaCubierto && (
            <Text fontSize="2xs" color={border} textAlign="center" lineClamp={1} opacity={0.85}>
              {iaName}
            </Text>
          )}
          {iaVoz && !estaCubierto && (
            <Text fontSize="2xs" color={border} textAlign="center" lineClamp={1} opacity={0.75} fontStyle="italic">
              {iaVoz}
            </Text>
          )}
        </VStack>
      </Box>
    </Tooltip>
  );
}

// ── Vista LIBRE ──────────────────────────────────────────────────────────────
function LibreReadOnly({ plantilla, posiciones, refuerzoMap }) {
  const canvasBg = useColorModeValue('gray.50', 'gray.900');
  const subtipo = plantilla.subtipoLibre || 'teatro';

  const placed = useMemo(
    () => posiciones.filter((p) => p.musico && p.x != null && p.y != null),
    [posiciones],
  );

  return (
    <Box position="relative" h="480px" bg={canvasBg} rounded="2xl" shadow="md" overflow="hidden">
      <Box position="absolute" inset={0} pointerEvents="none">
        {subtipo === 'calle' ? <CanvasGuiaCalle /> : <CanvasGuiaTeatro />}
      </Box>
      {placed.map((p) => (
        <Box
          key={p.id}
          position="absolute"
          left={`${p.x}%`}
          top={`${p.y}%`}
          transform="translate(-50%, -50%)"
        >
          <MusicoChipLibre musico={p.musico} asistencia={p.asistencia} refuerzo={refuerzoMap?.[p.id]} pos={p} />
        </Box>
      ))}
    </Box>
  );
}

// ── Extrae posicionId del marker [posicion:ID] en el campo notas ─────────────
const RE_POSICION = /^\[posicion:([^\]]+)\]/;

// ── Componente principal ─────────────────────────────────────────────────────
export default function FormacionViewer({ plantilla, posiciones, refuerzos }) {
  // Construye un mapa posicionId → refuerzo para las bajas cubiertas
  const refuerzoMap = useMemo(() => {
    const m = {};
    if (!refuerzos) return m;
    for (const r of refuerzos) {
      const match = r.notas && RE_POSICION.exec(r.notas);
      if (match) m[match[1]] = r;
    }
    return m;
  }, [refuerzos]);

  if (!plantilla) return null;
  const isGrid = (plantilla.modo || 'GRID') === 'GRID';
  return isGrid
    ? <GridReadOnly plantilla={plantilla} posiciones={posiciones} refuerzoMap={refuerzoMap} />
    : <LibreReadOnly plantilla={plantilla} posiciones={posiciones} refuerzoMap={refuerzoMap} />;
}
